/**
 * Hook per rendering progressivo di liste lunghe
 * @module flyers/hooks/useProgressiveList
 */

import React from "react";

/**
 * Hook che gestisce il rendering progressivo di liste lunghe
 * per migliorare le performance della UI
 * 
 * @param {Array} items - Array di elementi da renderizzare
 * @param {Object} options - Opzioni di configurazione
 * @param {boolean} options.enabled - Abilita il rendering progressivo (default: true)
 * @param {number} options.initialBatch - Numero elementi iniziali (default: 24)
 * @param {number} options.batchSize - Dimensione batch successivi (default: 24)
 * @param {number} options.idleTimeout - Timeout idle in ms (default: 80)
 * @param {number} options.threshold - Soglia minima per attivare (default: 36)
 * @returns {{ items: Array, isPending: boolean }} Elementi visibili e stato pending
 */
export const useProgressiveList = (
    items,
    {
        enabled = true,
        initialBatch = 24,
        batchSize = 24,
        idleTimeout = 80,
        threshold = 36
    } = {}
) => {
    const safeInitial = Math.max(1, initialBatch);
    const safeBatch = Math.max(1, batchSize);
    const safeTimeout = Math.max(0, idleTimeout);

    const shouldProgress = enabled && Array.isArray(items) && items.length > threshold;
    const [visibleCount, setVisibleCount] = React.useState(() => {
        if (!Array.isArray(items)) return 0;
        return shouldProgress ? Math.min(safeInitial, items.length) : items.length;
    });

    React.useEffect(() => {
        if (!Array.isArray(items)) {
            setVisibleCount(0);
            return;
        }

        const totalItems = items.length;

        if (!shouldProgress) {
            setVisibleCount(totalItems);
            return;
        }

        let cancelled = false;
        let idleId = null;
        let timeoutId = null;

        const schedule = () => {
            if (cancelled) return;

            const run = () => {
                setVisibleCount((prev) => {
                    if (prev >= totalItems) return prev;
                    const next = Math.min(prev + safeBatch, totalItems);
                    if (!cancelled && next < totalItems)
                        schedule();
                    return next;
                });
            };

            if (typeof window !== "undefined" && typeof window.requestIdleCallback === "function") {
                idleId = window.requestIdleCallback(() => {
                    idleId = null;
                    run();
                }, { timeout: safeTimeout });
                return;
            }

            timeoutId = setTimeout(() => {
                timeoutId = null;
                run();
            }, safeTimeout);
        };

        const lowerBound = Math.min(safeInitial, totalItems);
        let nextVisibleCount = null;
        setVisibleCount((prev) => {
            const base = Math.max(prev, lowerBound);
            const next = Math.min(base, totalItems);
            nextVisibleCount = next;
            return next;
        });

        if (nextVisibleCount != null && nextVisibleCount < totalItems)
            schedule();

        return () => {
            cancelled = true;
            if (typeof window !== "undefined" && idleId != null && typeof window.cancelIdleCallback === "function")
                window.cancelIdleCallback(idleId);
            if (timeoutId != null)
                clearTimeout(timeoutId);
        };
    }, [items, shouldProgress, safeInitial, safeBatch, safeTimeout]);

    return React.useMemo(() => {
        if (!Array.isArray(items))
            return { items: [], isPending: false };

        if (!shouldProgress) {
            return {
                items,
                isPending: false
            };
        }

        const visibleItems = items.slice(0, visibleCount);
        return {
            items: visibleItems,
            isPending: visibleItems.length < items.length
        };
    }, [items, shouldProgress, visibleCount]);
};

export default useProgressiveList;
